import { DictionaryRecordContextProvider } from "@/components/dictionaries/DictionaryContext";
import DictionaryForm from "@/components/dictionaries/DictionaryForm";
import DictionaryFormFields from "@/components/dictionaries/DictionaryFormFields";
import DictionaryLoader from "@/components/dictionaries/DictionaryLoader";
import DictionaryPageHeader from "@/components/dictionaries/DictionaryPageHeader";
import PageContent from "@/components/PageContent";
import { getDictionaryConfig } from "@/containers/dictionaries/getDictionaryConfig";
import { DictionaryContext } from "@/containers/dictionaries/types";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { useOfflineAwareMutation } from "@/lib/hooks/useOfflineAwareMutation";
import { ApiError } from "@/services/api";
import { ReadonlyObject } from "@/types/readOnly";
import { zodResolver } from "@hookform/resolvers/zod";
import { MutationFunction } from "@tanstack/react-query";
import { exists } from "i18next";
import { DefaultValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import SaveButton from "@/components/SaveButton";
import { useFormResetWithQueryData } from "@/hooks/useFormResetWithQueryData";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useToast } from "@/components/ui/use-toast";
import { useCachedQuery } from "@/hooks/useCachedQuery";
import { useCachedMutation } from "@/hooks/useCachedMutation";

export default function DictionaryEditor<
  TRead extends ReadonlyObject,
  TWrite extends object,
>({ id, dict }: DictionaryContext) {
  const { t } = useTranslation();
  const config = getDictionaryConfig(dict);
  const labels = config.getLabels(t);
  const Icon = config.Icon;
  const { toast } = useToast();
  const { can_update } = useModulePermissions();
  const handleOfflineAwareMutationSuccess = useOfflineAwareMutation<TRead>(
    config.id,
  );

  const query = useCachedQuery<TRead>({
    queryKey: [config.id, id],
    queryFn: async () => config.service.get(id),
  });

  const mutationFn: MutationFunction<TRead, TWrite> = async (values) =>
    config.service.patch<TWrite, TRead>(id, values);

  const mutation = useCachedMutation<TRead, ApiError, TWrite>({
    mutationFn,
    onSuccess: (response) => {
      if (query.data) {
        handleOfflineAwareMutationSuccess({
          type: "update",
          response,
          input: query.data,
          path: config.path,
          successMessage: labels.success.update,
        });
      }
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");

      toast({
        title: labels.errors.update,
        description,
        variant: "destructive",
      });
    },
  });

  const form = useForm<TWrite>({
    resolver: zodResolver(config.schema),
    defaultValues: (config.defaultValues || {}) as DefaultValues<TWrite>,
    disabled: mutation.isPending || !can_update,
  });

  useFormUnsavedChangesWarning(form);
  useFormResetWithQueryData(form, query);

  return (
    <DictionaryRecordContextProvider
      value={{
        config,
        query,
        mutation,
        form,
        id,
      }}
    >
      <DictionaryPageHeader />
      {query.isLoading && <DictionaryLoader />}
      {!query.isLoading && (
        <DictionaryForm>
          <PageContent
            icon={<Icon className="w-6 h-6 mr-3" />}
            footer={<SaveButton />}
            title={labels.title}
          >
            <DictionaryFormFields />
          </PageContent>
        </DictionaryForm>
      )}
    </DictionaryRecordContextProvider>
  );
}
